import { Link, createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WikiAnchorSection } from '@/components/wiki/WikiAnchorSection';
import { WikiToc } from '@/components/wiki/WikiToc';
import { GuideActionIcon } from '@/components/icons/GuideActionIcon';

export const Route = createFileRoute('/mystics/')({
  component: MysticsPage,
});

function MysticsPage() {
  const toc = [
    { id: 'mystics-overview', label: 'Overview' },
    { id: 'casting-basics', label: 'Casting Basics' },
    { id: 'spell-usage', label: 'Spell Usage' },
    { id: 'magic-actions', label: 'Magic Actions' },
  ];

  const actions = [
    'fireball', 'meteor_showers', 'nightmares', 'greed', 'storms', 'magic_shield', 'minor_protection',
    'mystic_aura', 'holy_vortex', 'land_lust', 'paradise', 'tree_of_gold', 'natures_blessing', 'reflect_magic',
  ];

  return (
    <div className="space-y-5">
      <WikiToc items={toc} />

      <WikiAnchorSection id="mystics-overview" title="Mystics and Arcane Pressure">
        <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
          <img src="/images/nav/mystics@3x.png" alt="Mystics" className="w-full max-w-[120px] rounded-xl border border-[#d4af37]/25" />
          <div className="space-y-3">
            <p>
              Magic in Mythea is a tempo lever: economy buffs, hostile disruption, defensive layering, and
              recovery acceleration. The strongest mystic play is coordinated and role-driven.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge>Rune economy</Badge>
              <Badge>WPA discipline</Badge>
              <Badge>Duration windows</Badge>
            </div>
          </div>
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="casting-basics" title="Casting Basics">
        <ul className="list-disc space-y-1 pl-5">
          <li>Spell reliability depends on readiness context, not only a single stat.</li>
          <li>Rune stock and regen planning should mirror war and aid timing.</li>
          <li>Some pressure spells scale with relation posture and target state.</li>
          <li>Self-support uptime is often the difference between stable and collapsing provinces.</li>
        </ul>
        <p>
          Related pages: <Link to="/science" className="text-[#e6c547] underline">Science</Link>,{' '}
          <Link to="/thievery" className="text-[#e6c547] underline">Thievery</Link>,{' '}
          <Link to="/kingdom-relations" className="text-[#e6c547] underline">Kingdom Relations</Link>.
        </p>
      </WikiAnchorSection>

      <WikiAnchorSection id="spell-usage" title="Spell Usage Notes (Formula-safe)">
        <p>
          This guide intentionally avoids exact outcome formulas. Instead, each spell page focuses on practical
          factors: when to cast, what the spell pressures, what counters it, and what recovery path defenders
          should execute.
        </p>
      </WikiAnchorSection>

      <WikiAnchorSection id="magic-actions" title="Magic Actions">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {actions.map(actionId => (
            <Link key={actionId} to="/actions/$actionId" params={{ actionId }}>
              <Card className="p-3 transition-colors hover:border-[#d4af37]/40">
                <div className="flex items-center gap-3">
                  <GuideActionIcon
                    code={actionId}
                    alt={actionId.replace(/_/g, ' ')}
                    width={48}
                    height={48}
                    className="h-12 w-12 overflow-hidden rounded-lg border border-[#d4af37]/25"
                  />
                  <span className="font-semibold text-[#e6c547]">{actionId.replace(/_/g, ' ')}</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>
    </div>
  );
}
